import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/design/components';
import { formatDate, weekdayName } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { useBranchStaff, useSwapMutations } from './api';

/**
 * הצעת משמרת להחלפה (PRD 5.4.7).
 *
 * ברירת המחדל היא הצעה פתוחה לכל מי שמתאים בסניף, כי זו הדרך המהירה
 * למצוא מחליף. הצעה אישית שולחת התראה לאדם אחד, ולכן היא בחירה
 * מפורשת ולא ברירת מחדל.
 */
export function SwapOfferDialog({
  assignmentId,
  date,
  shiftName,
  branchId,
  onClose,
}: {
  assignmentId: string;
  date: string;
  shiftName: string;
  branchId: string | undefined;
  onClose: () => void;
}) {
  const { request } = useSwapMutations();
  const { data: staff } = useBranchStaff(branchId);
  const [targetId, setTargetId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const field = 'min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink';

  return (
    <div className="fixed inset-0 z-30 grid items-end bg-charcoal/40 p-0 sm:place-items-center sm:p-4">
      <form
        role="dialog"
        aria-modal="true"
        aria-label="הצעת משמרת להחלפה"
        className="grid max-h-[85dvh] w-full gap-3 overflow-y-auto rounded-t-lg bg-surface p-4 sm:max-w-md sm:rounded-lg"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          request.mutate(
            { assignmentId, targetId: targetId || null, note },
            { onSuccess: onClose, onError: (caught) => setError(errorMessage(caught)) },
          );
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <h2 className="m-0 font-display text-display-m font-semibold">הצעה להחלפה</h2>
          <button
            type="button"
            aria-label="סגירה"
            onClick={onClose}
            className="grid size-11 cursor-pointer place-items-center bg-transparent text-ink"
          >
            <X size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>

        <p className="m-0 text-body text-ink">
          {weekdayName(date)} {formatDate(date)}
          {shiftName && <span className="text-muted"> · {shiftName}</span>}
        </p>

        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}

        <div className="grid gap-1">
          <label htmlFor="swap-target" className="text-label font-medium">
            למי להציע
          </label>
          <select
            id="swap-target"
            value={targetId}
            onChange={(event) => setTargetId(event.target.value)}
            className={field}
          >
            <option value="">לכל מי שמתאים בסניף</option>
            {(staff ?? []).map((person) => (
              <option key={person.id} value={person.id}>
                {person.first_name} {person.last_name}
              </option>
            ))}
          </select>
          <p className="m-0 text-body-sm text-muted">
            {targetId
              ? 'תישלח התראה אישית. רק הוא/היא יוכל/תוכל לקחת.'
              : 'כל עובד/ת בסניף יראה/תראה את ההצעה ויוכל/תוכל לקחת.'}
          </p>
        </div>

        <div className="grid gap-1">
          <label htmlFor="swap-note" className="text-label font-medium">
            למה (לא חובה)
          </label>
          <input
            id="swap-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="יש לי אירוע משפחתי"
            className={field}
          />
        </div>

        <p className="m-0 text-body-sm text-muted">
          המשמרת תעבור רק אחרי שמישהו ייקח ואישור מנהל/ת. עד אז היא עליך.
        </p>

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={request.isPending}>
            שליחת ההצעה
          </Button>
          <Button variant="secondary" onClick={onClose}>
            ביטול
          </Button>
        </div>
      </form>
    </div>
  );
}
